import moment from 'moment'
import _ from 'lodash'

/**
 * Sheep Utilities
 */
export default class Actions {
  /**
     * Maps an old field to a new field
     *
     * Params should be like:
     * {
     *     'Consignment Number': 'conn_note',
     *     'L': 'length',
     *     'Price': 'cost'
     * }
     */
  static map (input, output, params) {
    // For each input row
    for (var rowIndex in input) {
      var oldRow = input[rowIndex]
      var newRow = output[rowIndex]

      // For each of the params
      for (var oldKey in params) {
        var newKey = params[oldKey]
        // Map them
        newRow[newKey] = oldRow[oldKey]
      }
    }
  }

  /**
     * Loads a default column mapping
     *
     * Params should be 1 parameter:
     * 'columns' which should be an array of the default columns
     *
     */
  static defaultColumns (input, output, params) {
    var defaultColumns = params.columns

    // For each row
    for (var rowIndex in output) {
      for (var defaultColumnIndex in defaultColumns) {
        var defaultColumn = defaultColumns[defaultColumnIndex]
        // If it doesn't exist
        if (!(defaultColumn in output[rowIndex])) {
          // Map them
          output[rowIndex][defaultColumn] = null
        }
      }
    }
  }

  /**
     * Params should be 3 parameters:
     * 'column' which is the column to apply this to
     * 'from' the momentjs input format
     * 'output' the momentjs output format
     *
     * It will parse the field(s) with 'from' and format them as 'output'
     *
     */
  static formatDate (input, output, params) {
    // Get the vars
    var column = params.column
    var from = params.from
    var to = params.to
    // For each
    for (var rowIndex in output) {
      var row = output[rowIndex]
      if (column in row) {
        row[column] = moment(row[column], from).format(to)
      }
    }
  }

  /**
     * Params should be 3 parameters:
     * 'by' which is a delimtator
     * 'from' which is the original column key
     * 'to' which is an array of new column keys
     *
     * The code will:
     * Loops through each column
     * Loops through each exploded field
     * Sets the new column to be the new exploded field
     *
     * This would fail under some circumstances (e.g. exploded.length does not match from params.from.length)
     *
     */
  static explode (input, output, params) {
    var by = params.by
    var from = params.from
    var to = params.to

    // For each row
    for (var rowIndex in input) {
      try {
        // Get the new and old rows
        var oldRow = input[rowIndex]
        var newRow = output[rowIndex]
        // Explode the fields
        var exploded = oldRow[from].split(by)
        // For each of the exploded values
        for (var explodeIndex in exploded) {
          // In the new row
          // With the key of the current exploded index
          // Set it to the current exploded value
          newRow[to[explodeIndex]] = exploded[explodeIndex]
        }
      } catch (err) {
        // console.log('Failed to do explode on row ' + rowIndex)
      }
    }
  }

  /**
     * Params should be 2 parameters:
     * 'by' which is the key to group the rows by
     * 'sums' which is the columns which should be summed together
     */
  static mergeRows (input, output, params) {
    var by = params.by
    var fields = params.fields
    var largestOnlyFields = typeof params.largestOnlyFields !== 'undefined' ? params.largestOnlyFields : []

    // Firs we group the rows by the key
    var groups = _.groupBy(output, by)

    // This will store our newly resolved rows
    var newRows = []

    // Go through each group
    // Get a new single record with the first value(s)
    // For everything we sum loop through them
    // Calculate them together and set the result
    for (var groupKey in groups) {
      var group = groups[groupKey]
      var newRow = groups[groupKey][0] // Lets default to the first row values

      // For each
      for (var sumKey in fields) {
        var field = fields[sumKey]

        for (var rowKey in group) {
          if (rowKey > 0) { // Skip the first row, it's already included above
            var row = group[rowKey]

            // we always want numbers not strings
            // eslint-disable-next-line no-useless-escape
            newRow[field] = typeof newRow[field] === 'string' ? Number(newRow[field].replace(/[^0-9\.-]+/g, '')) : newRow[field]
            // eslint-disable-next-line no-useless-escape
            row[field] = typeof row[field] === 'string' ? Number(row[field].replace(/[^0-9\.-]+/g, '')) : row[field]

            // now we can run the if statement and add them together
            if (largestOnlyFields.includes(field)) {
              newRow[field] = _.round(Math.max(parseFloat(newRow[field]), parseFloat(row[field])), 4)
            } else {
              newRow[field] = _.round(parseFloat(newRow[field]) + parseFloat(row[field]), 4)
            }
          }
        }
      }

      newRows.push(newRow)
    }

    // Set new rows
    output.length = 0
    output.push.apply(output, newRows)
  }

  static mergeColumns (input, output, params) {
    let to = params.to
    let columns = params.columns

    // Loop through each row and column
    for (let rowIndex in output) {
      var row = output[rowIndex]

      let finalRowVal = 0
      for (let ci in columns) {
        if (typeof row[columns[ci]] === 'string') {
          // eslint-disable-next-line no-useless-escape
          finalRowVal += Number(row[columns[ci]].replace(/[^0-9\.-]+/g, ''))
        } else if (row[columns[ci]] === 'number') {
          finalRowVal += row[columns[ci]]
        }
      }

      row[to] = finalRowVal
    }
  }

  static removeColumns (input, output, params) {
    let columns = params.columns

    // Loop through each row and column
    // If the column is the one we're looking for
    // And the value is in what we're looking to remove
    // Record the row index
    for (let rowIndex in output) {
      for (let colIndex in columns) {
        delete output[rowIndex][columns[colIndex]]
      }
    }

    // // With our recorded row indexes
    // let offset = 0 // We use an offset as we splice out, each shifts out
    // for (let i in remove) {
    //     output.splice(parseInt(remove[i]) + offset, 1)
    //     offset--
    // }
  }

  static removeRows (input, output, params) {
    let column = params.column
    let values = params.values
    let removePartial = params.removePartial
    let remove = []

    // Loop through each row and column
    // If the column is the one we're looking for
    // And the value is in what we're looking to remove
    // Record the row index
    for (let i in output) {
      for (let j in output[i]) {
        if (j === column) {
          if (!removePartial) {
            if (values.includes(output[i][j])) {
              remove.push(i)
            }
          }

          if (removePartial) {
            for (let v in values) {
              if (output[i][j] !== null && output[i][j].includes(values[v])) {
                remove.push(i)
              }
            }
          }
        }
      }
    }

    // With our recorded row indexes
    let offset = 0 // We use an offset as we splice out, each shifts out
    for (let i in remove) {
      output.splice(parseInt(remove[i]) + offset, 1)
      offset--
    }
  }

  static removeAppendedChars (input, output, params) {
    let column = params.column
    let values = params.values

    // Loop through each row and column
    // Remove any appended characters from the values array passed
    for (let rowIndex in output) {
      var row = output[rowIndex]
      if (column in row) {
        if (row[column] !== null) {
          for (let val in values) {
            if (typeof row[column] === 'number') {
              row[column] = row[column].toString()
            }
            row[column] = values.includes(row[column].slice(-val.length)) ? row[column].slice(0, -val.length) : row[column]
          }
        }
      }
    }
  }

  static removePrependedChars (input, output, params) {
    let column = params.column
    let values = params.values

    // Loop through each row and column
    // Remove any appended characters from the values array passed
    for (let rowIndex in output) {
      var row = output[rowIndex]
      if (column in row) {
        if (row[column] !== null) {
          for (let val in values) {
            if (typeof row[column] === 'number') {
              row[column] = row[column].toString()
            }
            row[column] = values.includes(row[column].slice(0, val.length)) ? row[column].slice(val.length) : row[column]
          }
        }
      }
    }
  }

  static replaceText (input, output, params) {
    let column = params.column
    let replace = params.replace

    // Loop through each row and column
    // Remove any appended characters from the values array passed
    for (let rowIndex in output) {
      var row = output[rowIndex]
      if (column in row) {
        if (row[column] !== null && row[column] !== undefined) {
          for (let find in replace) {
            row[column] = _.includes(row[column], find) ? row[column].replace(find, replace[find]) : row[column]
          }
        }
      }
    }
  }

  static greaterOfRows (input, output, params) {
    let column = params.column
    let values = params.values

    // Loop through each row and column
    // Remove any appended characters from the values array passed
    for (let rowIndex in output) {
      var row = output[rowIndex]

      // set greatest as either row value or 0 if we can't get one
      var greatest = column in row ? row[column] : 0

      // loop over values and set greatest to the greatest val
      for (let valIndex in values) {
        var val = values[valIndex]
        greatest = row[val] > greatest ? row[val] : greatest
      }

      // set row.column the new greatested val
      if (column in row) {
        row[column] = greatest
      }
    }
  }

  static moveRowValues (input, output, params) {
    let from = params.from
    let to = params.to
    let trigger = params.trigger
    let triggerPartial = params.triggerPartial

    // Loop through each row and column
    for (let rowIndex in output) {
      var row = output[rowIndex]

      if (trigger.row in row) {
        if (row[trigger.row] !== null && row[trigger.row] !== undefined) {
          // if we have found the trigger exactly
          if (!triggerPartial && row[trigger.row] === trigger.val) {
            // set row.column the swapped val
            for (let i in to) {
              row[to] = row[from[i]]
              row[from] = ''
            }
          }

          // partial match trigger
          if (triggerPartial && row[trigger.row] !== null && row[trigger.row].includes(trigger.val)) {
            // set row.column the swapped val
            for (let i in to) {
              row[to] = row[from[i]]
              row[from] = ''
            }
          }
        }
      }
    }
  }

  static mapServices (input, output, params) {
    let column = params.column
    let mapping = params.mapping

    // Loop through each row and column
    // Remove any appended characters from the values array passed
    for (let rowIndex in output) {
      var row = output[rowIndex]
      if (column in row) {
        row[column] = typeof mapping[row[column]] !== 'undefined' ? mapping[row[column]] : null
      }
    }
  }
}
